import type { Party, WorldEvent } from '../engine/events'
import { formatMoney } from '../engine/money'
import type { DecisionMaker, EvaluationStep, ResponseCode } from '../engine/types'

/**
 * One model, three depths. The same state renders at every depth: detail
 * appears and disappears in place rather than sending the reader somewhere else.
 *
 * The model is deliberately Visa specific throughout. Mastercard and the
 * domestic schemes run equivalent services under different names, and mixing
 * the vocabularies is how explainers end up vague. See docs/sources.md.
 */
export type Depth = 0 | 1 | 2

export const DEPTH_NAMES: Record<Depth, string> = {
  0: 'Plain',
  1: 'Industry',
  2: 'Message',
}

export const DEPTH_BLURB: Record<Depth, string> = {
  0: 'No jargon. What a cardholder would recognise.',
  1: 'The words the industry actually uses.',
  2: 'Field level. Message types, DE 39 codes and the Visa system that handled it.',
}

/** The Visa systems each stage of the life cycle actually runs on. */
export const VISA_SYSTEMS = {
  network: 'VisaNet',
  authorisation: 'V.I.P. System',
  clearing: 'BASE II',
  settlement: 'Visa Settlement Service',
  risk: 'Visa Risk Manager',
  standIn: 'Stand-In Processing',
  scoring: 'Visa Advanced Authorization',
} as const

export const PARTY_LABEL: Record<Party, string> = {
  cardholder: 'Cardholder',
  merchant: 'Merchant',
  acquirer: 'Acquirer',
  scheme: 'Visa',
  issuer: 'Issuer',
}

export const PARTY_PLAIN: Record<Party, string> = {
  cardholder: 'You',
  merchant: 'The shop',
  acquirer: "The shop's bank",
  scheme: 'Visa',
  issuer: 'Your bank',
}

export const PARTY_ROLE: Record<Party, string> = {
  cardholder: 'Party one',
  merchant: 'Party two',
  acquirer: 'Party three',
  scheme: 'VisaNet',
  issuer: 'Party four',
}

export const PARTY_DESC: Record<Party, string> = {
  cardholder: 'Holds the Visa card and owes the money.',
  merchant: 'Takes the card and wants to be sure of being paid.',
  acquirer: "The merchant's bank. Accepts Visa transactions on its behalf and routes them.",
  scheme:
    'Not a bank and never holds the money. Switches authorisations through the V.I.P. System, clears through BASE II, and settles between the two banks.',
  issuer: "The cardholder's bank. Holds the money and makes the decision, unless it cannot.",
}

export const DECIDED_BY_LABEL: Record<DecisionMaker, string> = {
  issuer: 'The issuer decided',
  'scheme-vrm': 'Visa decided, applying the issuer’s Visa Risk Manager rules',
  'scheme-stip': 'Visa Stand-In Processing decided on the issuer’s behalf',
  'scheme-routing': 'VisaNet could not get an answer from the issuer',
}

export const DECIDED_BY_PLAIN: Record<DecisionMaker, string> = {
  issuer: 'Your bank decided this.',
  'scheme-vrm': 'Visa decided this, using rules your bank gave it. Your bank never saw it.',
  'scheme-stip': 'Your bank was unreachable, so Visa answered for it.',
  'scheme-routing': 'Nobody could reach your bank.',
}

/** DE 39 response codes, as carried in the Visa authorisation response. */
export const RESPONSE_CODES: Record<
  ResponseCode,
  { name: string; cardholderSaw: string; cause: string }
> = {
  '00': { name: 'Approved', cardholderSaw: 'Payment accepted.', cause: 'Every check passed.' },
  '05': {
    name: 'Do not honour',
    cardholderSaw: 'Card declined, with no reason given.',
    cause:
      'The catch-all. It covers a wide range of causes, which is why Visa presses issuers to use it less.',
  },
  '14': {
    name: 'Invalid account number',
    cardholderSaw: 'Card not recognised.',
    cause: 'No usable card record matched the credential presented.',
  },
  '46': {
    name: 'Closed account',
    cardholderSaw: 'This card no longer works.',
    cause: 'The account behind the card is closed. Nothing will ever approve.',
  },
  '51': {
    name: 'Insufficient funds',
    cardholderSaw: 'Not enough money.',
    cause: 'Available balance would not cover it. Note that this is available, not ledger.',
  },
  '54': {
    name: 'Expired card',
    cardholderSaw: 'Card has expired.',
    cause: 'Past the expiry date and not reissued. The account may well still be open.',
  },
  '57': {
    name: 'Transaction not permitted to cardholder',
    cardholderSaw: 'This card cannot be used here.',
    cause: 'A card control refused it. Merchant category, channel or geography.',
  },
  '59': {
    name: 'Suspected fraud',
    cardholderSaw: 'Card declined. Your bank may call you.',
    cause: 'A risk rule refused it, at the issuer or at Visa on the issuer’s behalf.',
  },
  '61': {
    name: 'Exceeds approval amount limit',
    cardholderSaw: 'Over your limit.',
    cause: 'A spend limit refused it before the balance was ever consulted.',
  },
  '62': {
    name: 'Restricted card',
    cardholderSaw: 'Card cannot be used.',
    cause: 'The card is not in a state that permits spending, or it is blocked at VisaNet.',
  },
  '65': {
    name: 'Exceeds withdrawal frequency limit',
    cardholderSaw: 'Too many payments today.',
    cause: 'A velocity or daily cumulative rule refused it.',
  },
  '91': {
    name: 'Issuer or switch inoperative',
    cardholderSaw: 'Cannot reach your bank, try again.',
    cause: 'The issuer did not answer and Stand-In Processing was not available to answer for it.',
  },
}

export const STEP_LABEL: Record<EvaluationStep, string> = {
  technical: 'Technical validation',
  'card-state': 'Card state',
  controls: 'Card controls',
  fraud: 'Fraud scoring',
  funds: 'Funds',
}

/** How the log reads at each depth. */
export function describeEvent(e: WorldEvent, depth: Depth): string {
  const m = (v: number) => formatMoney(v)

  switch (e.kind) {
    case 'world.reset':
      return depth === 0 ? 'Started again from the beginning.' : 'World reset to its initial state.'

    case 'day.advanced':
      return depth === 0 ? `A day passed. It is now day ${e.day}.` : `Clock advanced to day ${e.day}.`

    case 'config.changed':
      return `${e.label}: ${e.enabled ? 'on' : 'off'}.`

    case 'card.state-changed':
      if (depth === 0) return `The card went from ${e.from} to ${e.to}. ${e.reason}`
      if (depth === 1) return `Card state ${e.from} to ${e.to}. ${e.reason}`
      return `card.state: ${e.from} → ${e.to} (${e.cardId})`

    case 'auth.requested':
      if (depth === 0) return `${e.merchantName} asked whether you can pay ${m(e.amountMinor)}.`
      if (depth === 1)
        return `Authorisation requested, ${m(e.amountMinor)} at ${e.merchantName}, routed to VisaNet.`
      return `0100 authorisation request · amount ${m(e.amountMinor)} · V.I.P. System · ${e.transactionId}`

    case 'auth.decided': {
      const code = RESPONSE_CODES[e.responseCode]
      const who = depth === 0 ? DECIDED_BY_PLAIN[e.decidedBy] : DECIDED_BY_LABEL[e.decidedBy]
      if (depth === 0) {
        return e.outcome === 'declined'
          ? `Declined. ${code.cardholderSaw} ${who}`
          : `Approved for ${m(e.amountApprovedMinor)}. ${who}`
      }
      if (depth === 1) {
        return e.outcome === 'declined'
          ? `Declined, ${code.name}${e.category ? `, Visa category ${e.category}` : ''}. ${who}.`
          : `Approved for ${m(e.amountApprovedMinor)}. ${who}.`
      }
      return `0110 authorisation response · DE 39 = ${e.responseCode} ${code.name}${e.category ? ` · category ${e.category}` : ''} · by ${e.decidedBy}`
    }

    case 'hold.placed':
      if (depth === 0)
        return `${m(e.amountMinor)} was set aside.${e.advised ? '' : ' Your bank does not know about it yet.'}`
      if (depth === 1)
        return `Authorisation hold of ${m(e.amountMinor)} placed${e.advised ? '' : ', pending the Stand-In advice'}.`
      return `hold.open ${m(e.amountMinor)} · lapses day ${e.expiresDay} · advised=${e.advised} · ${e.transactionId}`

    case 'advice.delivered':
      if (depth === 0)
        return `Your bank was told about the ${m(e.amountMinor)} Visa approved for it.${e.overdrawn ? ' It turns out there was not enough money.' : ''}`
      if (depth === 1)
        return `Stand-In advice for ${m(e.amountMinor)} processed by the issuer.${e.overdrawn ? ' Account is now overdrawn.' : ''}`
      return `0120 authorisation advice · ${m(e.amountMinor)} · overdrawn=${e.overdrawn} · ${e.transactionId}`

    case 'hold.released': {
      const why =
        e.reason === 'cleared'
          ? depth === 0
            ? 'because the payment finally went through'
            : 'on presentment'
          : e.reason === 'expired'
            ? depth === 0
              ? 'because the shop never collected it in time'
              : 'on expiry, uncleared'
            : 'by reversal'
      if (depth === 0) return `The ${m(e.amountMinor)} set aside was let go, ${why}.`
      if (depth === 1) return `Hold of ${m(e.amountMinor)} released ${why}.`
      return `hold.${e.reason} ${m(e.amountMinor)} · ${e.transactionId}`
    }

    case 'clearing.posted': {
      const differs = e.amountMinor !== e.authAmountMinor
      const force = e.forcePost ? ' The hold had already lapsed, so this posted anyway.' : ''
      if (depth === 0) {
        const base = `${m(e.amountMinor)} actually left your account.`
        return differs
          ? `${base} That is not the ${m(e.authAmountMinor)} set aside.${force}`
          : base + force
      }
      if (depth === 1) {
        const base = `Presentment for ${m(e.amountMinor)} submitted through BASE II and posted to the ledger.`
        return differs ? `${base} Authorised amount was ${m(e.authAmountMinor)}.${force}` : base + force
      }
      return `TC 05 presentment ${m(e.amountMinor)} vs auth ${m(e.authAmountMinor)}${e.forcePost ? ' · force post' : ''} · BASE II · ${e.transactionId}`
    }

    case 'settlement.executed':
      if (depth === 0)
        return `The banks squared up through Visa. The shop was paid ${m(e.merchantNetMinor)} of your ${m(e.grossMinor)}. The rest went in fees.`
      if (depth === 1)
        return `Settled ${m(e.grossMinor)} through the Visa Settlement Service. Interchange reimbursement fee ${m(e.interchangeMinor)} retained by the issuer, Visa fees ${m(e.schemeFeeMinor)}, merchant net ${m(e.merchantNetMinor)}.`
      return `VSS settlement gross ${m(e.grossMinor)} · IRF ${m(e.interchangeMinor)} · Visa fees ${m(e.schemeFeeMinor)} · net ${m(e.merchantNetMinor)}`
  }
}

export function moneyNote(depth: Depth): string {
  if (depth === 0)
    return 'When a shop takes your card, the money does not move. Your bank sets it aside and promises the shop it will be paid. The money only really leaves days later, and the shop gets less than you paid.'
  if (depth === 1)
    return 'Authorisation reduces available balance and leaves the ledger untouched. Presentment posts to the ledger. Settlement moves funds between the banks, net of the interchange reimbursement fee and Visa fees. Three separate events, separated in time.'
  return 'Available = ledger less open holds. Ledger moves on presentment only. Merchant funding follows VSS settlement and is net of the merchant service charge.'
}
